export interface Column<T> { key: string; header: string; render: (row: T) => React.ReactNode; align?: 'left' | 'right'; key_stat?: boolean; minWidth?: number; hideSm?: boolean }

/**
 * Desktop: a table. Phone: one card per row (pass `mobile`) so nothing has to be
 * scrolled sideways to reach the number that matters.
 */
export function DataTable<T>({ columns, rows, rowKey, empty = 'No data.', mobile }: { columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string; empty?: string; mobile?: (row: T, i: number) => React.ReactNode }) {
  if (!rows.length) return <p className="body-muted py-6">{empty}</p>;
  return (
    <>
      {mobile && <div className="flex flex-col gap-3 md:hidden">{rows.map((r, i) => <div key={rowKey(r)}>{mobile(r, i)}</div>)}</div>}
      <div className={`card overflow-x-auto ${mobile ? 'hidden md:block' : ''}`}>
        <table className="w-full border-collapse text-[14px]">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ minWidth: c.minWidth }} className={`mono-label whitespace-nowrap px-4 py-3 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.hideSm ? 'hidden md:table-cell' : ''}`}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={rowKey(r)} className="border-t border-line/80 transition-colors hover:bg-white/70">
                {columns.map((c) => (
                  <td key={c.key} className={`whitespace-nowrap px-4 py-3 ${c.align === 'right' ? 'num text-right' : ''} ${c.key_stat ? 'text-blue' : ''} ${c.hideSm ? 'hidden md:table-cell' : ''}`}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** A labelled number for mobile row cards. */
export function Stat({ label, value, tone = '' }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="min-w-0">
      <p className="meta truncate !text-[11px]">{label}</p>
      <p className={`num mt-0.5 text-[16px] ${tone}`}>{value}</p>
    </div>
  );
}
